/**
 * Utility functions for managing axis session ranges in the layout state.
 *
 * @packageDocumentation
 * @module AxisSessionLayoutState
 * @public
 */

import { upgradeAxisPreferences } from '../config/plotPreferences';
import { AxisKey, AxisPreferences, PlotPreferencesV2 } from '../config/plotTypes';
import { getPlotlyLayoutIdToAxisKey } from './plotlySettings';
import { isAxisInAutoscaleMode } from './timeseriesAutoscaleYRanges';

// -- type definitions --
/**
 * Session zoom ranges for zoomable value axes, keyed by {@link AxisKey}.
 */
export type AxisSessionRanges = Partial<Record<AxisKey, [number, number]>>;

/** Input for merging timeseries value-axis layout state. */
export interface MergeTimeseriesValueAxisLayoutStateInput {
  readonly layoutState: Partial<Plotly.Layout>;
  readonly axisPrefs: Record<AxisKey, AxisPreferences>;
  readonly layoutIdToAxisKey: Record<string, AxisKey>;
  readonly axisSessionRanges?: AxisSessionRanges;
  readonly autoscaleYRanges: Partial<Record<AxisKey, [number, number]>>;
}

// -- utility functions --

/**
 * Builds a map from axis key to Plotly layout id.
 *
 * @param layoutIdToAxisKey - Plotly layout id to axis key map.
 * @returns Inverted lookup table.
 */
function buildAxisKeyToLayoutIdMap(
  layoutIdToAxisKey: Record<string, AxisKey>
): Record<AxisKey, string> {
  return Object.fromEntries(
    Object.entries(layoutIdToAxisKey).map(([layoutId, axisKey]) => [
      axisKey,
      layoutId,
    ])
  ) as Record<AxisKey, string>;
}

/**
 * Reads a numeric axis range from Plotly layout state.
 *
 * @param layoutState - Current layout state.
 * @param layoutId - Plotly layout id.
 * @returns Numeric range when valid.
 */
function readNumericLayoutAxisRange(
  layoutState: Partial<Plotly.Layout>,
  layoutId: string
): [number, number] | undefined {
  const existing = layoutState[layoutId as keyof Plotly.Layout] as
    | { range?: unknown }
    | undefined;
  const range = existing?.range;

  if (!Array.isArray(range) || range.length !== 2) {
    return undefined;
  }

  const start = Number(range[0]);
  const end = Number(range[1]);

  if (Number.isNaN(start) || Number.isNaN(end)) {
    return undefined;
  }

  return [start, end];
}

/**
 * Returns axis keys that use visible-window autoscale.
 *
 * @param axisPrefs - Plot axis preferences.
 * @returns Autoscale y-axis keys.
 */
export function getAutoscaleAxisKeys(
  axisPrefs: Record<AxisKey, AxisPreferences>
): AxisKey[] {
  return (Object.entries(axisPrefs) as [AxisKey, AxisPreferences][])
    .filter(([, axis]) => !axis.isXAxis && isAxisInAutoscaleMode(axis))
    .map(([axisKey]) => axisKey);
}

/**
 * Keeps session ranges only for axes with fixed or default bounds.
 *
 * @param axisSessionRanges - Host-owned session ranges.
 * @param axisPrefs - Plot axis preferences.
 * @returns Session ranges for bounded axes only.
 */
export function filterSessionRangesForBoundedAxes(
  axisSessionRanges: AxisSessionRanges,
  axisPrefs: Record<AxisKey, AxisPreferences>
): AxisSessionRanges {
  const boundedRanges: AxisSessionRanges = {};

  (Object.entries(axisSessionRanges) as [AxisKey, [number, number]][]).forEach(
    ([axisKey, range]) => {
      const axis = axisPrefs[axisKey];
      if (axis && !axis.isXAxis && !isAxisInAutoscaleMode(axis)) {
        boundedRanges[axisKey] = range;
      }
    }
  );

  return boundedRanges;
}

/**
 * Set the range of an axis in the layout state.
 */
function setLayoutAxisRange(
  layoutState: Partial<Plotly.Layout>,
  layoutId: string,
  range: [number, number] | [string, string]
): Partial<Plotly.Layout> {
  const existing = layoutState[layoutId as keyof Plotly.Layout] as
    | Record<string, unknown>
    | undefined;

  return {
    ...layoutState,
    [layoutId]: {
      ...(existing ?? {}),
      range,
    },
  };
}

/**
 * Build Plotly layout session entries from host-owned value-axis ranges.
 * 
 * Uses the {@link setLayoutAxisRange} function.
 */
export function buildLayoutStateFromAxisSessionRanges(
  axisSessionRanges: AxisSessionRanges,
  layoutIdToAxisKey: Record<string, AxisKey>
): Partial<Plotly.Layout> {
  const axisKeyToLayoutId = Object.fromEntries(
    Object.entries(layoutIdToAxisKey).map(([layoutId, axisKey]) => [
      axisKey,
      layoutId,
    ])
  ) as Record<AxisKey, string>;

  let state: Partial<Plotly.Layout> = {};

  (Object.entries(axisSessionRanges) as [AxisKey, [number, number]][]).forEach(
    ([axisKey, range]) => {
      const layoutId = axisKeyToLayoutId[axisKey];
      if (layoutId) {
        state = setLayoutAxisRange(state, layoutId, range);
      }
    }
  );

  return state;
}

/**
 * Merges bounded session ranges and visible-window autoscale y ranges.
 *
 * Manual y zoom on autoscale axes takes precedence until cleared by a
 * horizontal-only relayout.
 *
 * @param input - Layout state, axis prefs, session ranges, and autoscale ranges.
 * @returns Merged layout state for Plotly.
 */
export function mergeTimeseriesValueAxisLayoutState(
  input: MergeTimeseriesValueAxisLayoutStateInput
): Partial<Plotly.Layout> {
  const {
    layoutState,
    axisPrefs,
    layoutIdToAxisKey,
    axisSessionRanges,
    autoscaleYRanges,
  } = input;
  const axisKeyToLayoutId = buildAxisKeyToLayoutIdMap(layoutIdToAxisKey);
  let mergedState = { ...layoutState };

  if (axisSessionRanges) {
    const boundedSessionRanges = filterSessionRangesForBoundedAxes(
      axisSessionRanges,
      axisPrefs
    );
    mergedState = {
      ...mergedState,
      ...buildLayoutStateFromAxisSessionRanges(
        boundedSessionRanges,
        layoutIdToAxisKey
      ),
    };
  }

  getAutoscaleAxisKeys(axisPrefs).forEach((axisKey) => {
    const layoutId = axisKeyToLayoutId[axisKey];
    if (!layoutId) {
      return;
    }

    const manualRange =
      axisSessionRanges?.[axisKey] ??
      readNumericLayoutAxisRange(mergedState, layoutId);
    const computedRange = autoscaleYRanges[axisKey];

    if (manualRange) {
      mergedState = setLayoutAxisRange(mergedState, layoutId, manualRange);
      return;
    }

    if (computedRange) {
      mergedState = setLayoutAxisRange(mergedState, layoutId, computedRange);
      return;
    }

    mergedState = clearLayoutAxisRange(mergedState, layoutId);
  });

  return mergedState;
}

/**
 * Removes autoscale-axis session ranges after a horizontal-only relayout.
 *
 * @param axisSessionRanges - Host-owned session ranges.
 * @param axisPrefs - Plot axis preferences.
 * @returns Session ranges with autoscale entries removed.
 */
export function clearAutoscaleAxisSessionRanges(
  axisSessionRanges: AxisSessionRanges,
  axisPrefs: Record<AxisKey, AxisPreferences>
): AxisSessionRanges {
  const next = { ...axisSessionRanges };

  getAutoscaleAxisKeys(axisPrefs).forEach((axisKey) => {
    delete next[axisKey];
  });

  return next;
}

function clearLayoutAxisRange(
  layoutState: Partial<Plotly.Layout>,
  layoutId: string
): Partial<Plotly.Layout> {
  const existing = layoutState[layoutId as keyof Plotly.Layout] as
    | { range?: unknown }
    | undefined;

  if (!existing?.range) {
    return layoutState;
  }

  const { range: _range, ...rest } = existing;

  if (Object.keys(rest).length === 0) {
    const next = { ...layoutState };
    delete next[layoutId as keyof Plotly.Layout];
    return next;
  }

  return {
    ...layoutState,
    [layoutId]: rest,
  };
}

/**
 * Clears y-axis ranges stored in layout state for autoscale axes.
 *
 * @param layoutState - Current layout state.
 * @param axisPrefs - Plot axis preferences.
 * @param layoutIdToAxisKey - Plotly layout id to axis key map.
 * @returns Layout state without autoscale y ranges.
 */
export function clearAutoscaleAxisRangesInLayoutState(
  layoutState: Partial<Plotly.Layout>,
  axisPrefs: Record<AxisKey, AxisPreferences>,
  layoutIdToAxisKey: Record<string, AxisKey>
): Partial<Plotly.Layout> {
  const axisKeyToLayoutId = buildAxisKeyToLayoutIdMap(layoutIdToAxisKey);
  let next = layoutState;

  getAutoscaleAxisKeys(axisPrefs).forEach((axisKey) => {
    const layoutId = axisKeyToLayoutId[axisKey];
    if (layoutId) {
      next = clearLayoutAxisRange(next, layoutId);
    }
  });

  return next;
}

function isZoomableValueAxisOnDoubleClickReset(
  layoutId: string,
  axisKey: AxisKey,
  axisPrefs: Record<AxisKey, AxisPreferences>,
  plotType: 'timeseries' | 'scatter'
): boolean {
  if (plotType === 'timeseries' && layoutId === 'xaxis') {
    return false;
  }

  const axis = axisPrefs[axisKey];
  if (!axis) {
    return false;
  }

  return upgradeAxisPreferences(axis).zoomEnabled !== false;
}

/**
 * Drop session zoom for axes that double-click reset should restore
 * (default range or auto-scale). Fixed-range axes are left unchanged.
 */
export function clearZoomableAxisSessionRanges(
  axisSessionRanges: AxisSessionRanges,
  axisPrefs: Record<AxisKey, AxisPreferences>,
  layoutIdToAxisKey: Record<string, AxisKey>,
  plotType: 'timeseries' | 'scatter'
): AxisSessionRanges {
  const next = { ...axisSessionRanges };

  Object.entries(layoutIdToAxisKey).forEach(([layoutId, axisKey]) => {
    if (
      !isZoomableValueAxisOnDoubleClickReset(
        layoutId,
        axisKey,
        axisPrefs,
        plotType
      )
    ) {
      return;
    }

    delete next[axisKey];
  });

  return next;
}

/**
 * Clear zoomable value-axis session ranges for a plot config.
 */
export function clearZoomableAxisSessionRangesForPlot(
  axisSessionRanges: AxisSessionRanges,
  prefs: PlotPreferencesV2,
  plotType: 'timeseries' | 'scatter'
): AxisSessionRanges {
  return clearZoomableAxisSessionRanges(
    axisSessionRanges,
    prefs.axisPrefs,
    getPlotlyLayoutIdToAxisKey(prefs, plotType),
    plotType
  );
}

/**
 * Clear zoomable value-axis ranges from Plotly layout session state
 * (uncontrolled {@link ScatterPlotComponent} mode).
 */
export function clearZoomableAxisRangesInLayoutState(
  layoutState: Partial<Plotly.Layout>,
  axisPrefs: Record<AxisKey, AxisPreferences>,
  layoutIdToAxisKey: Record<string, AxisKey>,
  plotType: 'timeseries' | 'scatter'
): Partial<Plotly.Layout> {
  let next = layoutState;

  Object.entries(layoutIdToAxisKey).forEach(([layoutId, axisKey]) => {
    if (
      !isZoomableValueAxisOnDoubleClickReset(
        layoutId,
        axisKey,
        axisPrefs,
        plotType
      )
    ) {
      return;
    }

    next = clearLayoutAxisRange(next, layoutId);
  });

  return next;
}
